import React from 'react';
import { usePermission } from '@/hooks/usePermission';
import { useTenantStore } from '@/stores/tenantStore';
import { Forbidden } from '@/components/common/Forbidden';
import { scopeVisible, type NavScope } from '@/auth/scope';

interface RequireScopeProps {
  /** The plane this route belongs to (ADR-009 D7). */
  scope: NavScope;
  children: React.ReactNode;
}

/**
 * Plane (scope) guard — the route-level companion to the Sidebar's scope
 * filter (ADR-009 D7). Renders <Forbidden /> IN PLACE (no redirect) when the
 * caller is in the wrong plane for this route, so a deep link / stale URL
 * shows a clean Access-denied page instead of a half-loaded page firing a
 * doomed request.
 *
 * The cases it adds on top of <ProtectedRoute>'s permission check:
 *   - a platformAdmin with NO tenant selected hitting a tenant-scoped route
 *     → "Select a tenant first" (operating on tenant data with no tenant
 *     context is meaningless).
 *   - a tenant role hitting a platform-scoped route → Access denied (also
 *     already blocked by the platformAdmin-only permission, but this gives the
 *     clean page before any request fires).
 *
 * IMPORTANT (ADR-009 D7, review S3): this is UX / defense-in-depth, NOT the
 * security boundary. The real guard is the backend — `Permissions.TenantsManage`
 * (platformAdmin-only) plus ADR-008 claim binding, which 403 the call
 * regardless of what the UI renders. Never rely on <RequireScope> for
 * cross-plane security.
 */
export const RequireScope: React.FC<RequireScopeProps> = ({ scope, children }) => {
  const { isPlatformAdmin } = usePermission();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const hasTenantSelected = Boolean(selectedTenantId);

  if (!scopeVisible(scope, isPlatformAdmin, hasTenantSelected)) {
    // A platformAdmin who just hasn't picked a tenant gets actionable copy;
    // everyone else gets the canonical denial.
    const message =
      isPlatformAdmin && scope === 'tenant' && !hasTenantSelected
        ? 'Select a tenant from the picker to manage tenant-specific settings.'
        : undefined;
    return <Forbidden message={message} />;
  }

  return <>{children}</>;
};
